import React, { useCallback, MutableRefObject } from 'react';
import cx from 'classnames';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/LinkAsButton/LinkAsButton';
import Typography from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';
import { Badge } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Badge';
import { BadgeType } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Badge/types';
import { urls } from 'constants/urls';
import { usePodObserver } from 'hooks/use-pod-observer';
import { type Banner } from 'services/banners/types';
import styles from './AdtechCard.module.scss';

interface AdtechAnalyticsEvent extends AnalyticsEvent {
  /**
   * Urls to be invoked on SGTM
   */
  trackingUrls: string[];
  /**
   * The suppliers' product sponsorshipId
   */
  sponsorshipId: string;
  /**
   * The type of tracking event invoked
   */
  event: 'impression' | 'click';
  /**
   *  Opaque value supplied by the backend
   */
  vendor: string;
}

type AdtechTracker = (event: AdtechAnalyticsEvent) => void;

const makeUrl = (url: string | undefined, products: Banner['products']) => {
  if (products.length > 1) {
    const lineNumbers = products.map(({ lineNumber }) => lineNumber).join('%2C');

    return `${urls.buylist}/${lineNumbers}`;
  }

  return url;
};

export const AdtechCard = ({
  banner: { id, textArea, image, products, marketingBadge, tracking },
  className,
}: { banner: Banner } & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>) => {
  const { trackEvent }: { trackEvent: AdtechTracker } = useAnalytics();

  const observerRef: MutableRefObject<HTMLDivElement> = usePodObserver(
    useCallback(() => {
      trackEvent({
        event: 'impression',
        sponsorshipId: id,
        trackingUrls: tracking.impressionUrls,
        vendor: tracking.vendor,
      });
    }, [id, tracking.impressionUrls, tracking.vendor, trackEvent]),
  ) as unknown as MutableRefObject<HTMLDivElement>;

  const callbackRef = useCallback(
    (node: HTMLDivElement) => {
      observerRef.current = node;
    },
    [observerRef],
  );

  return (
    <div ref={callbackRef} className={cx(styles.ad, className)}>
      <img src={image.src} alt="" />
      {marketingBadge && (
        <Badge className={styles.badge} type={BadgeType.GREEN} text={marketingBadge} />
      )}
      <h3>
        <span className={styles.promo}>{textArea.offerHeading}</span> {textArea.heading}
      </h3>
      <Typography>{textArea.subheading}</Typography>
      <LinkAsButton
        href={makeUrl(textArea.link.url, products)}
        width="full"
        aria-label={textArea.link.altText}
        onClick={() => {
          trackEvent({
            event: 'click',
            sponsorshipId: id,
            trackingUrls: tracking.clickUrls,
            vendor: tracking.vendor,
          });
        }}
      >
        {textArea.link.text || 'Shop Now'}
      </LinkAsButton>
    </div>
  );
};
